/**
 * Derivations from a PlayerProfile payload into the shapes the cards render.
 *
 * Two rules from the brief drive most of this file:
 *  - §2.1 money is operator-side. GGR = stake - win, positive when the player
 *    loses. `pnl_kes` in the payload is player-perspective and negative; it is
 *    only ever shown as secondary text.
 *  - §2.2 percentage lists may sum to under 100. Never rescale — append an
 *    "Other" remainder bucket instead.
 */

export const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
export const DAY_LABELS = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

/** GGR for one { stake_kes, win_kes } segment. */
export const ggrOf = (segment) => (segment ? (segment.stake_kes || 0) - (segment.win_kes || 0) : 0);

/** Adds an "Other" remainder when a percentage list falls short of 100. */
export const withRemainder = (slices, labelKey = "label") => {
  const total = slices.reduce((sum, slice) => sum + (slice.pct || 0), 0);
  const remainder = Math.round((100 - total) * 100) / 100;
  if (remainder <= 0.05) return slices;
  return [...slices, { [labelKey]: "Other", pct: remainder, isRemainder: true }];
};

/** The six KPI-strip figures (§4.1), all traceable to pnl.totals / pnl.monthly. */
export const deriveKpis = (profile) => {
  const totals = profile?.pnl?.totals;
  if (!totals) return null;

  const casino = totals.casino || {};
  const sports = totals.sports || {};

  const turnover = (casino.stake_kes || 0) + (sports.stake_kes || 0);
  const bets = (casino.bets || 0) + (sports.bets || 0);
  const casinoGgr = ggrOf(casino);
  const sportsGgr = ggrOf(sports);
  const ggr = casinoGgr + sportsGgr;

  const latestMonth = lastOf(profile?.pnl?.monthly);
  const monthGgr = latestMonth ? ggrOf(latestMonth.casino) + ggrOf(latestMonth.sports) : null;

  const deposits = [...(profile?.pnl?.top_5_deposits_kes || [])].sort((a, b) => b - a);

  return {
    turnover,
    bets,
    ggr,
    playerNet: -ggr,
    hold: turnover ? (ggr / turnover) * 100 : 0,
    casinoHold: casino.stake_kes ? (casinoGgr / casino.stake_kes) * 100 : 0,
    sportsHold: sports.stake_kes ? (sportsGgr / sports.stake_kes) * 100 : 0,
    avgStake: bets ? turnover / bets : 0,
    monthGgr,
    monthKey: latestMonth?.month || null,
    deposits,
    largestDeposit: deposits.length ? deposits[0] : null,
    casino: { ...casino, ggr: casinoGgr },
    sports: { ...sports, ggr: sportsGgr },
    turnoverSplit: turnover
      ? {
          casinoPct: ((casino.stake_kes || 0) / turnover) * 100,
          sportsPct: ((sports.stake_kes || 0) / turnover) * 100,
        }
      : { casinoPct: 0, sportsPct: 0 },
  };
};

/** Flattens pnl.daily | weekly | monthly into one chart-ready series. */
export const deriveSeries = (profile, grain) => {
  const rows = (profile && profile.pnl && profile.pnl[grain]) || [];
  return rows.map((row) => {
    const casinoStake = row.casino?.stake_kes || 0;
    const sportsStake = row.sports?.stake_kes || 0;
    const stake = casinoStake + sportsStake;
    const wins = (row.casino?.win_kes || 0) + (row.sports?.win_kes || 0);
    const ggr = stake - wins;
    return {
      key: row.date || row.week_start || row.month,
      grain,
      casinoStake,
      sportsStake,
      stake,
      wins,
      ggr,
      hold: stake ? (ggr / stake) * 100 : 0,
      bets: (row.casino?.bets || 0) + (row.sports?.bets || 0),
    };
  });
};

/**
 * 7 x 24 heatmap grid. Intensity is that day's sessions_per_month, painted only
 * across the day's preferred_hours windows (§4.3); hours outside a window sit at
 * zero rather than being interpolated.
 */
export const deriveHeatmap = (playPatterns) => {
  if (!playPatterns) return null;

  const rows = DAY_ORDER.map((day) => {
    const pattern = playPatterns[day] || {};
    const sessions = pattern.sessions_per_month || 0;
    const hours = Array.from({ length: 24 }, () => 0);

    (pattern.preferred_hours || []).forEach((window) => {
      const [from, to] = parseWindow(window);
      for (let hour = from; hour < to; hour += 1) hours[hour] = sessions;
    });

    return { day, label: DAY_LABELS[day], sessions, hours, windows: pattern.preferred_hours || [] };
  });

  const peak = Math.max(1, ...rows.map((row) => row.sessions));
  return { rows, peak };
};

/** Merges casino + sports patterns for the heatmap's "Both" toggle. */
export const mergePlayPatterns = (casinoPatterns, sportsPatterns) => {
  if (!casinoPatterns) return sportsPatterns || null;
  if (!sportsPatterns) return casinoPatterns;

  return DAY_ORDER.reduce((merged, day) => {
    const a = casinoPatterns[day] || {};
    const b = sportsPatterns[day] || {};
    merged[day] = {
      // Sessions are the same rolling counter on both sides in the sample; take
      // the larger rather than summing, which would double-count a session in
      // which the player did both.
      sessions_per_month: Math.max(a.sessions_per_month || 0, b.sessions_per_month || 0),
      preferred_hours: Array.from(new Set([...(a.preferred_hours || []), ...(b.preferred_hours || [])])),
    };
    return merged;
  }, {});
};

/**
 * Category mix for the 100% bar: largest share first so the bar reads left to
 * right in order of importance, with the "Other" remainder always last.
 */
export const deriveCategoryMix = (categories) => {
  if (!categories || !categories.length) return [];
  const sorted = categories
    .map((category) => ({ label: category.name, pct: category.bet_share_pct || 0 }))
    .sort((a, b) => b.pct - a.pct);
  return withRemainder(sorted, "label");
};

/** Every game across every category/provider, sorted by overall bet share. */
export const deriveGames = (categories) => {
  if (!categories || !categories.length) return [];
  const games = [];
  categories.forEach((category) => {
    (category.providers || []).forEach((provider) => {
      (provider.games || []).forEach((game) => {
        games.push({
          ...game,
          provider: provider.name,
          category: category.name,
          id: `${category.name}-${provider.name}-${game.name}`,
        });
      });
    });
  });
  return games.sort((a, b) => (b.overall_bet_share_pct || 0) - (a.overall_bet_share_pct || 0));
};

/** The dominant game drives the cashout card — Aviator at 80% in the sample. */
export const dominantCrashGame = (games) => games.find((game) => game.cashout_ranges?.length) || null;

/** Plinko-style risk-level breakdowns, for the secondary mini bars. */
export const riskLevelGames = (games) => games.filter((game) => game.risk_levels?.length);

/** Flattens sports.by_sport into { sport, league, teams[] } for the favourites card. */
export const deriveFavourites = (bySport) => {
  if (!bySport) return [];
  return Object.entries(bySport).flatMap(([sport, sportNode]) =>
    Object.entries(sportNode.leagues || {}).map(([league, leagueNode]) => ({
      sport,
      league,
      teams: Object.entries(leagueNode.teams || {}).map(([team, teamNode]) => {
        const markets = Object.entries(teamNode.markets || {}).map(([market, weight]) => ({ market, weight }));
        return {
          team,
          markets: markets.sort((a, b) => b.weight - a.weight),
          // Market integers are relative bet counts (§2.3) — never currency.
          totalBets: markets.reduce((sum, entry) => sum + entry.weight, 0),
        };
      }),
    }))
  );
};

/**
 * True when the player has enough casino history to have a shape at all.
 *
 * This is what separates §7.4 from §7.3: a player with a full casino profile and
 * no sports bets genuinely has "no sportsbook activity" and Section D collapses,
 * whereas a player with no history anywhere is simply new, and every card keeps
 * its slot and shows the sparse empty state instead.
 */
export const hasCasinoProfile = (profile) => Boolean(profile?.casino?.categories?.length);

/** True when the profile has sportsbook activity worth a full section (§7.4). */
export const hasSportsActivity = (profile) => {
  const sports = profile?.sports;
  if (!sports) return false;
  const totalStake = profile?.pnl?.totals?.sports?.stake_kes || 0;
  const hasDistribution = [sports.preferred_odds_ranges, sports.preferred_bet_types, sports.preferred_stakes_kes].some(
    (list) => (list || []).some((entry) => (entry.pct || 0) > 0)
  );
  return totalStake > 0 || hasDistribution;
};

const lastOf = (list) => (list && list.length ? list[list.length - 1] : null);

/** "18:00-21:00" -> [18, 21]. A window that wraps past midnight stops at 24. */
function parseWindow(window) {
  const [start, end] = String(window).split("-");
  const from = Number(String(start).split(":")[0]);
  let to = Number(String(end).split(":")[0]);
  if (Number.isNaN(from) || Number.isNaN(to)) return [0, 0];
  if (to <= from) to = 24;
  return [from, Math.min(to, 24)];
}
